/*
 *  Copyright (C) 2024 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Config } from '../../config'
import { makeRequest } from '../../common/index'
import qs from 'qs'

const base_url = Config.admin.api_base_url
const context = Config.admin.context
const endpoint = `/${context}/webpage`

/**
 *  getWebpageVersionList
 *  @param {string} token
 *  @param {string} webpage
 *  @param {Object} params
 */
export const getWebpageVersionList = async function (token, webpage, params) {
    const method = 'GET'
    let path = `${endpoint}/${webpage}/version`
    if (params) {
        const page = params.page
        const pageSize = params.pageSize
        const query = qs.stringify(
            { pagination: { page, pageSize } }, { encodeValuesOnly: true }
        )
        path = `${path}?${query}`
    }
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * getWebpageVersion
 * @param {string} token
 * @param {string} webpage
 * @param {string} guid
 * @returns {Promise<*>}
 */
export const getWebpageVersion = async function (token, webpage, guid) {
    const method = 'GET'
    let path = `${endpoint}/${webpage}/version/${guid}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * getWebpagePackageVersion
 * @param {string} token
 * @param {string} webpage
 * @param {string} guid
 * @returns {Promise<*>}
 */
export const getWebpagePackageVersion = async function (token, webpage, guid) {
    const method = 'GET'
    let path = `${endpoint}/${webpage}/version/${guid}/package`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * getWebpageVersionLatest
 * @param {string} token
 * @param {string} webpage
 * @param {string} guid
 * @returns {Promise<*>}
 */
export const getWebpageVersionLatest = async function (token, webpage, guid) {
    const method = 'GET'
    let path = `${endpoint}/${webpage}/version/latest`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  searchWebpageVersion
 *  @param {string} token
 *  @param {string} webpage
 *  @param {string} searchTerm
 *  @param {Object} params
 *  @param {string} params.page
 *  @param {string} params.pageSize
 *  @param {string} params.sort
 *  @param {string} params.sortdir
 */
export const searchWebpageVersion = async function (token, webpage, searchTerm, params) {
    const method = 'GET'
    let path = `${endpoint}/${webpage}/version/search`
    if (params) {
        const page = params?.page
        const pageSize = params?.pageSize
        const sort = params?.sort
        const sortdir = params?.sortdir
        const query = qs.stringify(
            {
                term: searchTerm,
                pagination: { page, pageSize, sort, sortdir},
            },
            { encodeValuesOnly: true }
        )
        path = `${path}?${query}`
    }
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * createWebpageVersion
 * @param {string} token
 * @param {string} webpage
 * @param {object} payload
 * @returns {Promise<*>}
 */
export const createWebpageVersion = async function (token, webpage, payload) {
    const method = 'POST'
    const path = `${endpoint}/${webpage}/version`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * updateWebpageVersion
 * @param {string} token
 * @param {string} webpage
 * @param {string} version_pkg_id
 * @param {object} payload
 * @returns {Promise<*>}
 */
export const updateWebpageVersion = async function (token, webpage, version_pkg_id, payload) {
    const method = 'PATCH'
    const path = `${endpoint}/${webpage}/version/${version_pkg_id}`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  deleteWebpageVersion
 *  @param {string} token
 *  @param {string} webpage
 *  @param {string} version_pkg_id
 */
export const deleteWebpageVersion = async function (token, webpage, version_pkg_id) {
    const method = 'DELETE'
    const path = `${endpoint}/${webpage}/version/${version_pkg_id}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * restoreWebpageVersion
 * @param {string} token
 * @param {string} webpage
 * @param {string} version_pkg_id
 * @returns {Promise<*>}
 */
export const restoreWebpageVersion = async function (token, webpage, version_pkg_id) {
    const method = 'PATCH'
    const path = `${endpoint}/${webpage}/version/${version_pkg_id}/restore`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * favouriteWebpageVersion
 * @param {string} token
 * @param {string} webpage
 * @param {string} version_pkg_id
 * @param {object} payload
 * @returns {Promise<*>}
 */
export const favouriteWebpageVersion = async function (token, webpage, version_pkg_id, payload) {
    const method = 'PATCH'
    const path = `${endpoint}/${webpage}/version/${version_pkg_id}/favourite`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 * updateNameWebpageVersion
 * @param {string} token
 * @param {string} webpage
 * @param {string} version_pkg_id
 * @param {object} name
 * @returns {Promise<*>}
 */
export const updateNameWebpageVersion = async function (token, webpage, version_pkg_id, name) {
    const method = 'PATCH'
    const path = `${endpoint}/${webpage}/version/${version_pkg_id}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = { version_label: name }
    return await makeRequest(method, base_url, path, headers, payload)
}
